import { Fragment, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { linkHref } from './listingUrl';

// A URL that wraps AFTER a "/" or a "-" — the separator ending the line, never starting the
// next — and not in the middle of a word. A <wbr> after each is the break it may take; the
// browser offers none after a slash on its own, and one broken anywhere (`break-all`) cut
// "josette" in two. overflow-wrap:anywhere on the box stays the last resort, for a single
// run with no separator longer than the line.
function BreakAfterSeparators({ text }: { text: string }) {
  return (
    <>
      {text.split(/(?<=[/-])/).map((part, i) => (
        <Fragment key={i}>{part}<wbr /></Fragment>
      ))}
    </>
  );
}

// One listing link. EMPTY, it is a field to paste into, committed on blur or Enter: the
// stored URL is cleaned of its tracking tail, and rewriting the field under the cursor
// mid-type would fight whoever is typing. SET, it is the link itself — click it and the page
// opens — with a cross to remove it. Not editable in place: a wrong link is removed and the
// right one pasted, which is what editing a URL amounts to anyway.
function ListingInput({
  value,
  onCommit,
  onRemove,
  disabled,
  placeholder,
  openTitle,
  removeTitle,
}: {
  value: string;
  onCommit: (value: string) => void;
  onRemove: () => void;
  disabled?: boolean;
  placeholder: string;
  openTitle: string;
  removeTitle: string;
}) {
  const [draft, setDraft] = useState('');
  const href = linkHref(value);
  // Padded on BOTH sides though only the right holds the cross: the text is centred, and
  // centring it inside a lopsided box puts it off the middle of the one thing the eye
  // measures it against — the border.
  const boxCls =
    'block w-full rounded-lg border border-slate-300 bg-white px-9 py-2 text-center text-sm';

  if (!value) {
    return (
      <input
        type="url"
        inputMode="url"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft.trim()) onCommit(draft);
          setDraft('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
        className={`${boxCls} min-w-0 text-slate-800 placeholder:text-slate-400 disabled:opacity-50`}
      />
    );
  }
  return (
    // min-w-0: a flex item refuses to shrink below its content — so without it every row
    // was as wide as its own URL, and four links to one product made four boxes of four
    // different widths. Allowed to shrink, they are all the width of the column and wrap.
    <div className="relative w-full min-w-0">
      {/* THE WHOLE ADDRESS, wrapped as many lines as it takes: a path has no spaces, and any
          shortening cut off the slug that says which product it is. */}
      <a
        href={href || undefined}
        target="_blank"
        rel="noopener noreferrer"
        title={openTitle}
        className={`${boxCls} leading-snug text-slate-800 [overflow-wrap:anywhere] transition-colors hover:border-blue-400 hover:text-blue-600`}
      >
        <BreakAfterSeparators text={value} />
      </a>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        title={removeTitle}
        aria-label={removeTitle}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-rose-600 disabled:opacity-50"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// One listing link and a spinner while it reads. The Link table's cell, one per submodel
// (see ListingLinksTable).
export function LinkRow({
  value,
  onCommit,
  onRemove,
  detecting,
  disabled,
  placeholder,
  openTitle,
  removeTitle,
  error,
}: {
  value: string;
  onCommit: (value: string) => void;
  onRemove: () => void;
  detecting?: boolean;
  disabled?: boolean;
  placeholder: string;
  openTitle: string;
  removeTitle: string;
  error?: string;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-1">
      <div className="flex w-full items-center gap-1.5">
        <ListingInput
          value={value}
          onCommit={onCommit}
          onRemove={onRemove}
          disabled={disabled || detecting}
          placeholder={placeholder}
          openTitle={openTitle}
          removeTitle={removeTitle}
        />
        {detecting && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />}
      </div>
      {error && (
        <p className="w-full text-center text-xs text-rose-600">{error}</p>
      )}
    </div>
  );
}
